import PlayIcon from "@/assets/colorable/play.svg?react";
import Loader from "@/components/Common/kpi/Loader";
import clsxm from "@/lib/clsxm";
import {useCarouselStore} from "@/store/carousel";
import {shouldMuteVideosAtom} from "@/store/jotaiAtoms";
import type {TInstagramMedia} from "@/store/types";
import {useSetAtom} from "jotai";
import {type Dispatch, type FC, type SetStateAction, useEffect, useState} from "react";

const CarouselAlbumItem: FC<{
  className?: string;
  content: TInstagramMedia;
  isHoverable?: boolean;
  setHoveredAlbumItem: Dispatch<SetStateAction<TInstagramMedia | null>>;
}> = ({className, content, isHoverable, setHoveredAlbumItem}) => {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const setShouldMuteVideos = useSetAtom(shouldMuteVideosAtom);
  const initialTimestamp = useCarouselStore(state => state.initialTimestamp);
  const setCarouselValue = useCarouselStore(state => state.setValue);

  useEffect(() => {
    setIsVideoReady(false);
  }, [content.media_file]);

  return (
    <div
      className={clsxm(
        "max-w-full overflow-hidden rounded-lg",
        isHoverable && "flex size-12 shrink-0 items-center justify-center sm:size-20",
        className,
      )}
      onMouseEnter={() => {
        if (isHoverable) {
          setHoveredAlbumItem(content);
        }
      }}>
      {content.media_type === "IMAGE" || !content.media_file ? (
        <div className={clsxm("flex max-w-full justify-center", isHoverable && "size-full")}>
          <img
            src={content.media_file ?? content.thumbnail_file!}
            className={clsxm(
              "max-h-[45vh] max-w-full object-contain sm:max-h-[60vh]",
              isHoverable && "size-full object-cover",
            )}
          />
        </div>
      ) : (
        <div className={clsxm("relative max-w-full", isHoverable && "size-full")}>
          {isHoverable ? (
            <>
              <div className="border-neutral-white-dark bg-neutral-white-dark/20 absolute top-1/2 left-1/2 z-10 flex size-12 -translate-x-1/2 -translate-y-1/2 transform items-center justify-center rounded-full border">
                <PlayIcon className="size-6 pl-1 text-white" />
              </div>
              <img src={content.thumbnail_file!} className="size-full object-cover" />
            </>
          ) : (
            <>
              <img
                src={content.thumbnail_file!}
                className={clsxm("max-h-[45vh] max-w-full object-contain sm:max-h-[60vh]", isVideoReady && "invisible")}
              />
              {!isVideoReady && (
                <Loader
                  styles={{
                    containerClassName: "absolute inset-0 items-center",
                    loaderClassName: "size-10 text-white",
                  }}
                />
              )}
              <video
                autoPlay
                className={clsxm(
                  "absolute inset-0 size-full max-h-[45vh] max-w-full object-contain transition-opacity duration-150 sm:max-h-[60vh]",
                  isVideoReady ? "opacity-100" : "opacity-0",
                )}
                controls
                controlsList="nodownload"
                loop
                muted
                onCanPlay={e => {
                  setIsVideoReady(true);
                  if (initialTimestamp !== null) {
                    e.currentTarget.currentTime = initialTimestamp;
                    setCarouselValue("initialTimestamp", null);
                  }
                }}
                onVolumeChange={e => setShouldMuteVideos(e.currentTarget.muted || e.currentTarget.volume === 0)}
                playsInline
                src={content.media_file!}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default CarouselAlbumItem;
